import { ShieldCheck, FileText, FolderOpen, HeartPulse, Info } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";

const TOPICS = [
  { icon: ShieldCheck, title: "Health Insurance Basics", desc: "Understand common insurance terms and concepts." },
  { icon: FileText, title: "Cashless Hospitalization", desc: "Learn about the general cashless process and important documentation." },
  { icon: FolderOpen, title: "Healthcare Documentation", desc: "Understand why medical and insurance-related documents may be required." },
  { icon: HeartPulse, title: "Healthcare Awareness", desc: "Explore practical healthcare information for individuals and families." },
];

export const Patients = () => (
  <section id="patients" className="relative bg-white py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <SectionTag index="07" label="For Patients & General Public" />
        <h2 className="font-display mt-6 max-w-3xl text-2xl font-bold tracking-tight text-[#0B3C5C] sm:text-3xl lg:text-4xl">
          Making Healthcare Information Easier to Understand
        </h2>
        <p className="mt-5 max-w-2xl text-base text-[#4A6B82] sm:text-lg">
          Healthcare terminology, insurance processes and hospital procedures can sometimes feel
          complicated. Our Healthcare Knowledge initiative aims to simplify important information
          through easy-to-understand educational content.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {TOPICS.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.07}>
            <div
              data-testid={`patient-topic-${i + 1}`}
              className="group h-full rounded-2xl border border-[#0B3C5C]/10 bg-[#EAF3F8]/60 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/30"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/10 transition-colors duration-300 group-hover:bg-amber-500/20">
                <t.icon className="h-5 w-5 text-amber-500" />
              </span>
              <h3 className="font-display mt-5 text-base font-semibold text-[#0B3C5C]">{t.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#4A6B82]">{t.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div className="mt-10 flex flex-wrap items-center gap-5">
          <a
            href="/knowledge"
            data-testid="patients-cta-knowledge"
            className="rounded-full bg-amber-500 px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-amber-400 hover:shadow-xl hover:shadow-amber-500/20"
          >
            Visit Healthcare Knowledge
          </a>
        </div>
        <div
          data-testid="patients-disclaimer"
          className="mt-10 flex items-start gap-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6"
        >
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="text-xs leading-relaxed text-[#4A6B82] sm:text-sm">
            <p className="font-semibold text-amber-400">Important Note</p>
            <p className="mt-2">
              Healthcare information published by Healthcare Solutions is intended for general
              awareness and educational purposes. It is not a substitute for medical diagnosis,
              treatment or professional medical advice.
            </p>
            <p className="mt-2">
              Insurance and claim-related information is subject to the applicable policy terms,
              insurer/TPA processes and final decisions of the relevant parties.
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
